<script>
  import { ButtonGroup, GridContainer, Link, Rule } from "@ObamaFoundation/of-design-system";
  import CalendarHeader from "./CalendarHeader.svelte";
  import { fly } from "svelte/transition";
  import { cubicInOut } from "svelte/easing";
  import { getTotalNumberOfSelectedFilters } from "$lib/utilities/filters";
  import { getActiveFilterLabel } from "$lib/utilities/eventFilters";

  export let id = "calendar-hero";

  /**
   * Classes to style the container element
   * @type {string}
   */
  export let containerClasses = "";

  /**
   * Function to call when the filter link is clicked
   * @type {function}
   */
  export let filterListIsOpen = false;

  /**
   * The currently selected month in YYYY-MM-DD format
   * @type {string}
   */
  export let selectedMonth = undefined;

  /**
   * The list of years and months to display in the dropdown
   * @type {import('./CalendarHeader.svelte').CalendarHeaderYear[]}
   */
  export let years = [];

  /**
   * The currently selected format (view-as-grid or view-as-list)
   */
  export let selectedFormat = "view-as-grid";

  /**
   * Whether the hero is static (non-interactive). This occurs for months after the current month.
   */
  export let isStaticHero = false;

  /**
   * Filters currently active in the calendar
   * @type {Array[{Object}]}
   */
  export let activeFilters = [];

  $: numActiveFilters = getTotalNumberOfSelectedFilters(activeFilters);
  $: activeFilterLabel = getActiveFilterLabel(activeFilters, numActiveFilters);

  const handleMonthChange = (evt) => {
    if (evt.currentTarget.value) {
      selectedMonth = evt.currentTarget.value;
    }
  };

  const formatDateToMonth = (dateString = "") => {
    const [year, month, day] = dateString.split("-").map(Number);
    // month is 0-based in JS Date
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      month: "long"
    });
  };

  const handleButtonGroupSelection = (evt) => {
    selectedFormat = evt.detail.selection;
  };

  const handleFilterClick = () => {
    filterListIsOpen = true;
  };

  $: title = isStaticHero ? title : formatDateToMonth(selectedMonth);
</script>

<div
  in:fly|global={{ duration: 500, ease: cubicInOut, y: 30, delay: 500 }}
  out:fly|global={{ duration: 500, ease: cubicInOut, y: -20, delay: 100 }}
>
  <GridContainer
    {id}
    containerElement="section"
    containerClass="pt-15 md:pt-20 xl:pt-25 {containerClasses}"
  >
    <div class="md:flex md:justify-between md:items-end">
      <CalendarHeader
        {title}
        currentMonth={selectedMonth}
        {years}
        onMonthChange={handleMonthChange}
        hideDropdown={isStaticHero}
      />
      <Rule type="thick" class="mt-6 mb-4 md:hidden" />
      {#if !isStaticHero}
        <div class="flex sm:max-md:justify-between md:gap-x-10 md:mb-2 lg:mb-3 xl:mb-4">
          <Link
            aria-label={`Filters (${numActiveFilters} applied)`}
            class="text-ellipsis overflow-hidden whitespace-nowrap !inline-block max-w-[calc(100vw-180px)] md:max-w-none"
            variant="small"
            trailingIcon="caretDown"
            on:click={handleFilterClick}
          >
            Filter
            {#if numActiveFilters > 0}
              &nbsp;|&nbsp; {activeFilterLabel}
            {/if}
          </Link>
          <ButtonGroup
            on:buttonGroupSelection={handleButtonGroupSelection}
            selection={selectedFormat}
            buttons={[
              { id: "view-as-grid", ariaLabel: "View as grid", iconName: "grid" },
              { id: "view-as-list", ariaLabel: "View as list", iconName: "list" }
            ]}
          />
        </div>
      {/if}
    </div>
    <Rule type="thick" class="sm:max-md:hidden md:mt-7.5 lg:mt-10 xl:mt-15" />
  </GridContainer>
</div>
